'use client';

import { useMemo, useState } from 'react';
import type { RadarBlip, RadarQuadrant, RadarRing } from '@/lib/tech-radar/types';
import { RING_CONFIG, QUADRANT_CONFIG } from '@/lib/tech-radar/types';

interface RadarVisualizationProps {
  blips: RadarBlip[];
  size?: number;
  onBlipClick?: (blip: RadarBlip) => void;
  selectedBlipId?: string | null;
}

export function RadarVisualization({
  blips,
  size = 600,
  onBlipClick,
  selectedBlipId,
}: RadarVisualizationProps) {
  const [hoveredBlip, setHoveredBlip] = useState<RadarBlip | null>(null);

  const center = size / 2;
  const maxRadius = center - 60; // Leave room for labels

  // Calculate ring radii
  const ringRadii = useMemo(() => {
    const rings: RadarRing[] = ['adopt', 'trial', 'assess', 'hold'];
    const step = maxRadius / rings.length;
    return rings.reduce((acc, ring, idx) => {
      acc[ring] = step * (idx + 1);
      return acc;
    }, {} as Record<RadarRing, number>);
  }, [maxRadius]);

  // Position blips within their quadrant and ring
  const positionedBlips = useMemo(() => {
    const quadrants: RadarQuadrant[] = ['techniques', 'platforms', 'tools', 'languages_frameworks'];

    // Group blips by quadrant and ring
    const grouped: Record<RadarQuadrant, Record<RadarRing, RadarBlip[]>> = {
      techniques: { adopt: [], trial: [], assess: [], hold: [] },
      platforms: { adopt: [], trial: [], assess: [], hold: [] },
      tools: { adopt: [], trial: [], assess: [], hold: [] },
      languages_frameworks: { adopt: [], trial: [], assess: [], hold: [] },
    };

    blips.forEach((blip) => {
      grouped[blip.quadrant][blip.ring].push(blip);
    });

    // Calculate positions
    const positioned: Array<{
      blip: RadarBlip;
      x: number;
      y: number;
    }> = [];

    quadrants.forEach((quadrant, quadrantIdx) => {
      const quadrantAngle = QUADRANT_CONFIG[quadrant].angle;
      const angleStart = (quadrantAngle * Math.PI) / 180;
      const angleSpan = Math.PI / 2; // 90 degrees per quadrant

      const rings: RadarRing[] = ['adopt', 'trial', 'assess', 'hold'];

      rings.forEach((ring, ringIdx) => {
        const blipsInSegment = grouped[quadrant][ring];
        const innerRadius = ringIdx === 0 ? 0 : ringRadii[rings[ringIdx - 1]];
        const outerRadius = ringRadii[ring];
        const ringMidRadius = (innerRadius + outerRadius) / 2;

        blipsInSegment.forEach((blip, blipIdx) => {
          // Distribute blips within the segment
          const segmentAngleStart = angleStart + 0.1; // Small padding
          const segmentAngleEnd = angleStart + angleSpan - 0.1;
          const angleStep = (segmentAngleEnd - segmentAngleStart) / Math.max(blipsInSegment.length, 1);
          const angle = segmentAngleStart + angleStep * (blipIdx + 0.5);

          // Add some randomness to radius for visual variety
          const radiusVariation = (outerRadius - innerRadius) * 0.3;
          const radius = ringMidRadius + (Math.sin(blip.id.charCodeAt(5) * 1000) * radiusVariation);

          const x = center + radius * Math.cos(angle);
          const y = center + radius * Math.sin(angle);

          positioned.push({ blip, x, y });
        });
      });
    });

    return positioned;
  }, [blips, center, ringRadii]);

  return (
    <div className="relative">
      <svg width={size} height={size} className="bg-background/50 rounded-lg">
        {/* Rings */}
        {(['hold', 'assess', 'trial', 'adopt'] as RadarRing[]).map((ring) => (
          <circle
            key={ring}
            cx={center}
            cy={center}
            r={ringRadii[ring]}
            fill="none"
            stroke={RING_CONFIG[ring].color}
            strokeOpacity={0.3}
            strokeWidth={1}
          />
        ))}

        {/* Ring fills (for hover effect) */}
        {(['adopt', 'trial', 'assess', 'hold'] as RadarRing[]).map((ring, idx) => {
          const prevRing: RadarRing | null = idx > 0 ? ['adopt', 'trial', 'assess'][idx - 1] as RadarRing : null;
          const innerRadius = prevRing ? ringRadii[prevRing] : 0;
          const outerRadius = ringRadii[ring];

          return (
            <circle
              key={`fill-${ring}`}
              cx={center}
              cy={center}
              r={(innerRadius + outerRadius) / 2}
              fill={RING_CONFIG[ring].color}
              fillOpacity={0.05}
              strokeWidth={(outerRadius - innerRadius)}
              stroke={RING_CONFIG[ring].color}
              strokeOpacity={0.02}
            />
          );
        })}

        {/* Quadrant lines */}
        <line
          x1={center}
          y1={60}
          x2={center}
          y2={size - 60}
          stroke="rgba(232, 213, 183, 0.2)"
          strokeWidth={1}
        />
        <line
          x1={60}
          y1={center}
          x2={size - 60}
          y2={center}
          stroke="rgba(232, 213, 183, 0.2)"
          strokeWidth={1}
        />

        {/* Ring labels */}
        {(['adopt', 'trial', 'assess', 'hold'] as RadarRing[]).map((ring, idx) => {
          const prevRing: RadarRing | null = idx > 0 ? ['adopt', 'trial', 'assess'][idx - 1] as RadarRing : null;
          const innerRadius = prevRing ? ringRadii[prevRing] : 0;
          const radius = (innerRadius + ringRadii[ring]) / 2;

          return (
            <text
              key={`label-${ring}`}
              x={center + radius}
              y={center - 5}
              fill={RING_CONFIG[ring].color}
              fontSize={11}
              fontWeight={500}
              textAnchor="middle"
              opacity={0.8}
            >
              {RING_CONFIG[ring].label}
            </text>
          );
        })}

        {/* Quadrant labels */}
        <text x={center + maxRadius / 2} y={35} fill="#E8D5B7" fontSize={12} textAnchor="middle" fontWeight={600}>
          {QUADRANT_CONFIG.platforms.icon} Platforms
        </text>
        <text x={size - 35} y={center} fill="#E8D5B7" fontSize={12} textAnchor="middle" fontWeight={600} transform={`rotate(90, ${size - 35}, ${center})`}>
          {QUADRANT_CONFIG.tools.icon} Tools
        </text>
        <text x={center - maxRadius / 2} y={size - 25} fill="#E8D5B7" fontSize={12} textAnchor="middle" fontWeight={600}>
          {QUADRANT_CONFIG.languages_frameworks.icon} Languages
        </text>
        <text x={35} y={center} fill="#E8D5B7" fontSize={12} textAnchor="middle" fontWeight={600} transform={`rotate(-90, 35, ${center})`}>
          {QUADRANT_CONFIG.techniques.icon} Techniques
        </text>

        {/* Blips */}
        {positionedBlips.map(({ blip, x, y }) => {
          const isSelected = selectedBlipId === blip.id;
          const isHovered = hoveredBlip?.id === blip.id;
          const ringColor = RING_CONFIG[blip.ring].color;

          return (
            <g key={blip.id}>
              {/* Blip circle */}
              <circle
                cx={x}
                cy={y}
                r={isSelected || isHovered ? 12 : 8}
                fill={ringColor}
                stroke={isSelected ? '#E8D5B7' : 'none'}
                strokeWidth={isSelected ? 2 : 0}
                opacity={isSelected || isHovered ? 1 : 0.8}
                className="cursor-pointer transition-all duration-150"
                onClick={() => onBlipClick?.(blip)}
                onMouseEnter={() => setHoveredBlip(blip)}
                onMouseLeave={() => setHoveredBlip(null)}
              />
              {/* New indicator */}
              {blip.isNew && (
                <circle
                  cx={x + 6}
                  cy={y - 6}
                  r={4}
                  fill="#E89841"
                  stroke="#0A1E2E"
                  strokeWidth={1}
                />
              )}
              {/* Movement indicator */}
              {blip.movedFrom && (
                <text x={x} y={y + 3} textAnchor="middle" fill="#0A1E2E" fontSize={10} fontWeight={700}>
                  {blip.movedFrom === 'adopt' ? '↓' : blip.movedFrom === 'hold' ? '↑' : '→'}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hoveredBlip && (
        <div
          className="absolute bg-background border border-border/30 rounded-lg p-3 shadow-xl z-10 pointer-events-none"
          style={{
            left: '50%',
            bottom: '100%',
            transform: 'translateX(-50%)',
            marginBottom: 10,
            minWidth: 200,
          }}
        >
          <div className="font-medium text-foreground">{hoveredBlip.name}</div>
          <div className="text-xs text-foreground-tertiary mt-1">
            <span style={{ color: RING_CONFIG[hoveredBlip.ring].color }}>
              {RING_CONFIG[hoveredBlip.ring].label}
            </span>
            {' · '}
            {QUADRANT_CONFIG[hoveredBlip.quadrant].label}
          </div>
          {hoveredBlip.description && (
            <div className="text-xs text-foreground-tertiary mt-2 line-clamp-2">{hoveredBlip.description}</div>
          )}
        </div>
      )}
    </div>
  );
}
